import { useModalQueue } from '@carvana/merch-react-hooks';
import deepmerge from 'deepmerge';
import React, { createContext, useMemo } from 'react';
import type { DeepPartial } from '~/types';
import { ModalId, ModalPriorities } from './constants';

type Toggler = (isOpen: boolean) => void;
type ContextType = {
  isAccountModalOpen: boolean;
  isAddCoApplicantModalOpen: boolean;
  isAnyModalOpen: boolean;
  isEditCoApplicantModalOpen: boolean;
  isSoftPullModalOpen: boolean;
  setAccountModalOpen: Toggler;
  setAddCoApplicantModalOpen: Toggler;
  setEditCoApplicantModalOpen: Toggler;
  setSoftPullModalOpen: Toggler;
};

export const defaultContext: ContextType = {
  isAccountModalOpen: false,
  isAddCoApplicantModalOpen: false,
  isAnyModalOpen: false,
  isEditCoApplicantModalOpen: false,
  isSoftPullModalOpen: false,
  setAccountModalOpen() {},
  setAddCoApplicantModalOpen() {},
  setEditCoApplicantModalOpen() {},
  setSoftPullModalOpen() {}
};

const ModalContext = createContext<ContextType>(defaultContext);

export function ModalContextProvider({ children }: React.PropsWithChildren) {
  const [currentModalId, generateOpener] = useModalQueue(ModalPriorities);

  const {
    setAccountModalOpen,
    setAddCoApplicantModalOpen,
    setEditCoApplicantModalOpen,
    setSoftPullModalOpen
  } = useMemo(
    () => ({
      setAccountModalOpen: generateOpener(ModalId.Account),
      setAddCoApplicantModalOpen: generateOpener(ModalId.AddCoApplicant),
      setEditCoApplicantModalOpen: generateOpener(ModalId.EditCoApplicant),
      setSoftPullModalOpen: generateOpener(ModalId.SoftPull)
    }),
    [generateOpener]
  );

  const value = useMemo<ContextType>(
    (): ContextType => ({
      isAccountModalOpen: currentModalId === ModalId.Account,
      isAddCoApplicantModalOpen: currentModalId === ModalId.AddCoApplicant,
      isAnyModalOpen: currentModalId > -1,
      isEditCoApplicantModalOpen: currentModalId === ModalId.EditCoApplicant,
      isSoftPullModalOpen: currentModalId === ModalId.SoftPull,
      setAccountModalOpen,
      setAddCoApplicantModalOpen,
      setEditCoApplicantModalOpen,
      setSoftPullModalOpen
    }),
    [
      currentModalId,
      setAccountModalOpen,
      setAddCoApplicantModalOpen,
      setEditCoApplicantModalOpen,
      setSoftPullModalOpen
    ]
  );

  return (
    <ModalContext.Provider value={value}>{children}</ModalContext.Provider>
  );
}

ModalContextProvider.displayName = 'ModalContextProvider';

export default ModalContext;

export const generateModalContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);

export function useModalContext() {
  return React.useContext(ModalContext);
}
